import { useState, useEffect, useRef, useCallback } from "react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useServices } from "@/hooks/useServices";
import { useSales } from "@/hooks/useSales";
import { useCustomers } from "@/hooks/useCustomers";
import { useQueryClient } from "@tanstack/react-query";

import {
  Search,
  Plus,
  Trash2,
  Loader2,
  Banknote,
  Smartphone,
  Clock,
  X,
  User,
  Phone,
  Receipt,
  IndianRupee,
  Sparkles,
  Package,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface InvoiceData {
  id: string;
  invoiceNumber?: string;
  customerName?: string;
  customerPhone?: string;
  items: Array<{
    serviceName: string;
    quantity: number;
    unitPrice: number;
    originalPrice?: number;
    discount?: number;
    totalPrice: number;
  }>;
  totalAmount: number;
  totalDiscount?: number;
  receivedAmount: number;
  dueAmount: number;
  paymentMode: string;
  paymentStatus: string;
  createdAt: string;
}

interface QuickSaleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onInvoiceCreated?: (invoice: InvoiceData) => void;
}

interface CartItem {
  serviceId: string | null;
  serviceName: string;
  quantity: number;
  unitPrice: number;
  costPrice: number;
  totalPrice: number;
}

const paymentModes = [
  { id: "cash", label: "Cash", icon: Banknote },
  { id: "upi", label: "UPI", icon: Smartphone },
  { id: "unpaid", label: "Due", icon: Clock },
];

const formatCurrency = (amount: number) => `₹${amount.toLocaleString("en-IN")}`;

export function QuickSaleModal({ open, onOpenChange, onSuccess, onInvoiceCreated }: QuickSaleModalProps) {
  const { toast } = useToast();
  const { user, tenant, setBillingInProgress, isSessionReady } = useAuth();
  const { activeServices, createService } = useServices();
  const { createSale } = useSales();
  const { createCustomer, searchCustomerByMobile, searchCustomersByName } = useCustomers();
  const queryClient = useQueryClient();

  const searchInputRef = useRef<HTMLInputElement>(null);

  // State
  const [cart, setCart] = useState<CartItem[]>([]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [remarks, setRemarks] = useState("");
  const [frequentServiceNames, setFrequentServiceNames] = useState<string[]>([]);
  
  // Customer details
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    name: string;
    mobile: string;
    total_spent: number;
    total_due: number;
  } | null>(null);
  const [customerSearchResults, setCustomerSearchResults] = useState<Array<{
    id: string;
    name: string;
    mobile: string;
    total_spent: number;
    total_due: number;
  }>>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  
  // Instant add state
  const [instantSellPrice, setInstantSellPrice] = useState("");
  const [instantCostPrice, setInstantCostPrice] = useState("");
  const [isCreatingService, setIsCreatingService] = useState(false);

  // Editable total
  const [editableTotal, setEditableTotal] = useState("");
  const [isEditingTotal, setIsEditingTotal] = useState(false);

  const totalAmount = cart.reduce((sum, item) => sum + item.totalPrice, 0);

  // Sync editable total with calculated total
  useEffect(() => {
    if (!isEditingTotal) {
      setEditableTotal(totalAmount.toString());
    }
  }, [totalAmount, isEditingTotal]);

  // Fetch frequent services
  useEffect(() => {
    const fetchFrequentServices = async () => {
      if (!open || !tenant?.id) return;
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("sale_items")
        .select("service_name, sales!inner(tenant_id)")
        .eq("sales.tenant_id", tenant.id)
        .order("created_at", { ascending: false })
        .limit(100);
      
      if (data) {
        const frequency: Record<string, number> = {};
        data.forEach(item => {
          frequency[item.service_name] = (frequency[item.service_name] || 0) + 1;
        });
        const sorted = Object.entries(frequency)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([name]) => name);
        setFrequentServiceNames(sorted);
      }
    };
    fetchFrequentServices();
  }, [open, tenant?.id]);

  // Auto-focus search on open
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { handleClose(); return; }
      if (e.key === "Enter" && e.ctrlKey && cart.length > 0 && !isSubmitting) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, cart, isSubmitting]);

  const resetForm = () => {
    setCart([]);
    setPaymentMode("cash");
    setSearchQuery("");
    setRemarks("");
    setInstantSellPrice("");
    setInstantCostPrice("");
    setCustomerName("");
    setCustomerPhone("");
    setSelectedCustomer(null);
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    setEditableTotal("");
    setIsEditingTotal(false);
  };

  const handleClose = () => { resetForm(); onOpenChange(false); };

  // Customer search with debounce
  const handleCustomerNameChange = async (value: string) => {
    setCustomerName(value);
    setSelectedCustomer(null); // Clear selection when typing
    
    if (value.trim().length >= 1) {
      setIsSearchingCustomers(true);
      setShowCustomerDropdown(true);
      try {
        const results = await searchCustomersByName(value.trim());
        setCustomerSearchResults(results.map(c => ({
          id: c.id,
          name: c.name,
          mobile: c.mobile,
          total_spent: c.total_spent,
          total_due: c.total_due,
        })));
      } catch (error) {
        console.error("Error searching customers:", error);
        setCustomerSearchResults([]);
      } finally {
        setIsSearchingCustomers(false);
      }
    } else {
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
    }
  };

  const handleSelectCustomer = (customer: typeof customerSearchResults[0]) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerPhone(customer.mobile);
    setShowCustomerDropdown(false);
    setCustomerSearchResults([]);
  };

  const addToCart = useCallback((service: { id: string | null; name: string; price: number; costPrice?: number }) => {
    const existingIndex = cart.findIndex((item) => item.serviceName === service.name);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      newCart[existingIndex].totalPrice = newCart[existingIndex].quantity * newCart[existingIndex].unitPrice;
      setCart(newCart);
    } else {
      setCart([...cart, {
        serviceId: service.id,
        serviceName: service.name,
        quantity: 1,
        unitPrice: service.price,
        costPrice: service.costPrice || 0,
        totalPrice: service.price,
      }]);
    }
    setSearchQuery("");
    setInstantSellPrice("");
    setInstantCostPrice("");
    searchInputRef.current?.focus();
  }, [cart]);

  const removeFromCart = (index: number) => setCart(cart.filter((_, i) => i !== index));

  const updateQuantity = (index: number, delta: number) => {
    const newCart = [...cart];
    const newQty = newCart[index].quantity + delta;
    if (newQty < 1) { removeFromCart(index); return; }
    newCart[index].quantity = newQty;
    newCart[index].totalPrice = newQty * newCart[index].unitPrice;
    setCart(newCart);
  };

  const updateItemPrice = (index: number, field: 'unitPrice' | 'costPrice', value: string) => {
    const numValue = parseFloat(value) || 0;
    const newCart = [...cart];
    newCart[index][field] = numValue;
    if (field === 'unitPrice') {
      newCart[index].totalPrice = newCart[index].quantity * numValue;
    }
    setCart(newCart);
  };

  const updateItemTotal = (index: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    const newCart = [...cart];
    newCart[index].totalPrice = numValue;
    // Recalculate unit price
    if (newCart[index].quantity > 0) {
      newCart[index].unitPrice = numValue / newCart[index].quantity;
    }
    setCart(newCart);
  };

  // Handle total edit with proportional distribution
  const handleTotalEdit = (value: string) => {
    setEditableTotal(value);
    const newTotal = parseFloat(value) || 0;
    if (cart.length > 0 && totalAmount > 0) {
      const ratio = newTotal / totalAmount;
      const newCart = cart.map(item => ({
        ...item,
        unitPrice: item.unitPrice * ratio,
        totalPrice: item.totalPrice * ratio,
      }));
      setCart(newCart);
    }
  };

  // Instant add service
  const handleInstantAdd = async () => {
    if (!searchQuery.trim() || !instantSellPrice) return;
    
    const sellPrice = parseFloat(instantSellPrice);
    const costPrice = parseFloat(instantCostPrice) || 0;
    
    if (isNaN(sellPrice) || sellPrice <= 0) {
      toast({ title: "Invalid price", variant: "destructive" });
      return;
    }

    setIsCreatingService(true);
    try {
      if (createService) {
        await createService.mutateAsync({
          tenant_id: tenant?.id || "",
          name: searchQuery.trim(),
          default_price: sellPrice,
          cost_price: costPrice,
          category: "Other",
        });
      }
      
      addToCart({ id: null, name: searchQuery.trim(), price: sellPrice, costPrice: costPrice });
      toast({ title: "✨ Service Added", description: `"${searchQuery.trim()}" saved & added` });
    } catch {
      addToCart({ id: null, name: searchQuery.trim(), price: sellPrice, costPrice: costPrice });
    } finally {
      setIsCreatingService(false);
    }
  };

  // Filtered services
  const isSearching = searchQuery.trim().length > 0;
  const filteredServices = isSearching
    ? activeServices?.filter(s =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.category?.toLowerCase().includes(searchQuery.toLowerCase())
      ) || []
    : [];

  const frequentServices = activeServices?.filter(s => 
    frequentServiceNames.includes(s.name)
  ).sort((a, b) => frequentServiceNames.indexOf(a.name) - frequentServiceNames.indexOf(b.name)) || [];

  const showInstantAdd = isSearching && filteredServices.length === 0;

  // Submit
  const handleSubmit = async () => {
    if (!isSessionReady && !user?.id) {
      toast({ title: "Session Error", description: "Please refresh.", variant: "destructive" });
      return;
    }
    
    setBillingInProgress(true);
    const userId = user?.id;
    const tenantId = tenant?.id;
    
    if (!userId || !tenantId) {
      toast({ title: "Error", description: "Session invalid", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }
    
    if (cart.length === 0) {
      toast({ title: "Error", description: "Add at least one item", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const finalTotal = parseFloat(editableTotal) || totalAmount;
      let cashAmount = 0, onlineAmount = 0;
      let receivedAmount = finalTotal, dueAmount = 0, paymentStatus = "paid";
      
      if (paymentMode === "cash") {
        cashAmount = finalTotal;
      } else if (paymentMode === "upi") {
        onlineAmount = finalTotal;
      } else if (paymentMode === "unpaid") {
        receivedAmount = 0;
        dueAmount = finalTotal;
        paymentStatus = "unpaid";
      }

      const totalProfit = cart.reduce((sum, item) => sum + (item.totalPrice - (item.costPrice * item.quantity)), 0);
      
      // Use selected customer or auto-create if name and phone provided
      let finalCustomerId: string | null = selectedCustomer?.id || null;
      const trimmedName = customerName.trim();
      const trimmedPhone = customerPhone.trim();
      
      // Only auto-create if no customer selected and valid name + 10-digit phone provided
      if (!finalCustomerId && trimmedName && trimmedPhone.length === 10) {
        try {
          // First check if customer already exists with this phone
          const existingCustomer = await searchCustomerByMobile(trimmedPhone);
          if (existingCustomer) {
            finalCustomerId = existingCustomer.id;
            console.log('[QuickSaleModal] Found existing customer:', existingCustomer.id);
          } else {
            // Create new customer
            const newCustomer = await createCustomer({
              name: trimmedName,
              mobile: trimmedPhone,
            });
            finalCustomerId = newCustomer.id;
            
            // Invalidate customer queries
            queryClient.invalidateQueries({ queryKey: ["customers"] });
            queryClient.invalidateQueries({ queryKey: ["customers-paginated"] });
            queryClient.invalidateQueries({ queryKey: ["customers-stats"] });
            queryClient.invalidateQueries({ queryKey: ["infinite-customers"] });
            
            console.log('[QuickSaleModal] New customer created:', newCustomer.id, trimmedName);
          }
        } catch (error: any) {
          // Handle duplicate mobile error
          if (error?.message?.includes('duplicate') || error?.code === '23505') {
            try {
              const existingCustomer = await searchCustomerByMobile(trimmedPhone);
              if (existingCustomer) {
                finalCustomerId = existingCustomer.id;
                console.log('[QuickSaleModal] Found existing customer by mobile:', existingCustomer.id);
              }
            } catch (searchError) {
              console.error("Error searching existing customer:", searchError);
            }
          } else {
            console.error("Error creating customer:", error);
          }
          // Continue with sale even if customer creation fails
        }
      }
      
      const saleResult = await createSale(
        {
          tenant_id: tenantId,
          user_id: userId,
          customer_id: finalCustomerId,
          customer_name: trimmedName || null,
          customer_phone: trimmedPhone || null,
          invoice_number: null,
          total_amount: finalTotal,
          total_discount: 0,
          received_amount: receivedAmount,
          due_amount: dueAmount,
          cash_amount: cashAmount,
          online_amount: onlineAmount,
          total_profit: totalProfit,
          payment_mode: paymentMode,
          payment_status: paymentStatus,
          is_deleted: false,
          deleted_at: null,
          deleted_reason: null,
          notes: remarks.trim() || null,
        },
        cart.map((item) => ({
          service_id: item.serviceId,
          service_name: item.serviceName,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          original_price: item.unitPrice,
          discount_amount: 0,
          total_price: item.totalPrice,
          cost_price: item.costPrice * item.quantity,
          profit: item.totalPrice - (item.costPrice * item.quantity),
        }))
      );

      if (onInvoiceCreated && saleResult) {
        onInvoiceCreated({
          id: saleResult.id,
          invoiceNumber: saleResult.invoice_number || undefined,
          customerName: customerName.trim() || undefined,
          customerPhone: customerPhone.trim() || undefined,
          items: cart.map(item => ({
            serviceName: item.serviceName,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            totalPrice: item.totalPrice,
          })),
          totalAmount: finalTotal,
          receivedAmount,
          dueAmount,
          paymentMode,
          paymentStatus,
          createdAt: new Date().toISOString(),
        });
      }

      toast({ title: "✅ Bill Created!", description: `${formatCurrency(finalTotal)} • ${paymentMode.toUpperCase()}` });
      handleClose();
      onSuccess();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to create sale", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
      setBillingInProgress(false);
    }
  };

  const ModalContent = (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 sm:pb-3 border-b shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Receipt className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold">New Sale</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">Quick billing</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={handleClose} className="rounded-full h-8 w-8 sm:h-10 sm:w-10">
          <X className="h-4 w-4 sm:h-5 sm:w-5" />
        </Button>
      </div>

      {/* Customer Details */}
      <div className="pt-3 sm:pt-4 pb-2 sm:pb-3 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {/* Customer Name with Autocomplete */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Input
              placeholder="Customer Name (Optional)"
              value={customerName}
              onChange={(e) => handleCustomerNameChange(e.target.value)}
              onFocus={() => customerSearchResults.length > 0 && setShowCustomerDropdown(true)}
              onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
              className="pl-10 h-10 sm:h-11 rounded-xl text-sm sm:text-base"
              autoComplete="off"
            />
            {/* Customer Search Dropdown */}
            {showCustomerDropdown && (customerSearchResults.length > 0 || isSearchingCustomers) && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {isSearchingCustomers ? (
                  <div className="p-3 text-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Searching...
                  </div>
                ) : customerSearchResults.length > 0 ? (
                  customerSearchResults.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full px-3 py-2 text-left hover:bg-accent transition-colors flex items-center justify-between gap-2 border-b last:border-b-0"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{customer.name}</div>
                        <div className="text-xs text-muted-foreground">{customer.mobile}</div>
                      </div>
                      <div className="text-right shrink-0">
                        {customer.total_due > 0 && (
                          <span className="text-xs text-destructive font-medium">
                            ₹{customer.total_due.toLocaleString()} due
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                ) : null}
              </div>
            )}
          </div>
          
          {/* Phone Input */}
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Phone (Optional)"
              value={customerPhone}
              onChange={(e) => {
                setCustomerPhone(e.target.value);
                if (selectedCustomer && e.target.value !== selectedCustomer.mobile) {
                  setSelectedCustomer(null);
                }
              }}
              className="pl-10 h-10 sm:h-11 rounded-xl text-sm sm:text-base"
              type="tel"
            />
          </div>
        </div>
        
        {/* Selected Customer Info Card */}
        {selectedCustomer && (
          <div className="bg-accent/50 border border-border rounded-xl px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{selectedCustomer.name}</div>
                <div className="text-xs text-muted-foreground">{selectedCustomer.mobile}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0 text-xs">
              <div className="text-center">
                <div className="text-muted-foreground">Paid</div>
                <div className="font-semibold text-green-600">₹{selectedCustomer.total_spent.toLocaleString()}</div>
              </div>
              {selectedCustomer.total_due > 0 && (
                <div className="text-center">
                  <div className="text-muted-foreground">Due</div>
                  <div className="font-semibold text-destructive">₹{selectedCustomer.total_due.toLocaleString()}</div>
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => {
                  setSelectedCustomer(null);
                  setCustomerName("");
                  setCustomerPhone("");
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="pb-2 sm:pb-3 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Search service or type new..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10 sm:h-12 text-sm sm:text-base rounded-xl border-2 focus-visible:ring-primary/20"
          />
          {searchQuery && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 sm:h-8 sm:w-8 rounded-full" 
              onClick={() => { setSearchQuery(""); setInstantSellPrice(""); setInstantCostPrice(""); }}
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <ScrollArea className="flex-1 min-h-0 -mx-1 px-1">
        <div className="space-y-4 pb-2">
          
          {/* Instant Add - Shows when no results */}
          {showInstantAdd && (
            <div className="bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 border-2 border-dashed border-primary/40 rounded-2xl p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-primary">New Service</p>
                  <p className="text-sm text-muted-foreground">"{searchQuery}"</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Selling Price *</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="0"
                      value={instantSellPrice}
                      onChange={(e) => setInstantSellPrice(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleInstantAdd()}
                      className="pl-9 h-12 text-lg font-bold rounded-xl"
                      autoFocus
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Cost Price</Label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      inputMode="numeric"
                      placeholder="0"
                      value={instantCostPrice}
                      onChange={(e) => setInstantCostPrice(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleInstantAdd()}
                      className="pl-9 h-12 text-lg rounded-xl"
                    />
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleInstantAdd}
                disabled={!instantSellPrice || isCreatingService}
                className="w-full h-12 rounded-xl text-base font-semibold gap-2"
                size="lg"
              >
                {isCreatingService ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-5 w-5" />
                    Add to Bill
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Search Results */}
          {isSearching && filteredServices.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground px-1">Results</p>
              <div className="grid grid-cols-2 gap-2">
                {filteredServices.slice(0, 8).map((service) => (
                  <button
                    key={service.id}
                    className="p-4 rounded-xl border-2 border-transparent bg-card hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
                    onClick={() => addToCart({
                      id: service.id,
                      name: service.name,
                      price: Number(service.effective_price),
                      costPrice: Number(service.effective_cost_price || 0),
                    })}
                  >
                    <p className="font-medium text-sm truncate group-hover:text-primary">{service.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-primary font-bold">{formatCurrency(Number(service.effective_price))}</span>
                      {Number(service.effective_cost_price) > 0 && (
                        <span className="text-xs text-muted-foreground">Cost: {formatCurrency(Number(service.effective_cost_price))}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Frequent Services */}
          {!isSearching && frequentServices.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground px-1">Quick Pick</p>
              <div className="grid grid-cols-2 gap-2">
                {frequentServices.map((service) => (
                  <button
                    key={service.id}
                    className="p-4 rounded-xl border-2 border-transparent bg-card hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
                    onClick={() => addToCart({
                      id: service.id,
                      name: service.name,
                      price: Number(service.effective_price),
                      costPrice: Number(service.effective_cost_price || 0),
                    })}
                  >
                    <p className="font-medium text-sm truncate group-hover:text-primary">{service.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-primary font-bold">{formatCurrency(Number(service.effective_price))}</span>
                      {Number(service.effective_cost_price) > 0 && (
                        <span className="text-xs text-muted-foreground">Cost: {formatCurrency(Number(service.effective_cost_price))}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cart Items */}
          {cart.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <p className="text-sm font-semibold">Cart ({cart.length})</p>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => setCart([])}>
                  Clear All
                </Button>
              </div>
              
              <div className="space-y-2">
                {cart.map((item, index) => (
                  <div key={index} className="p-4 rounded-xl bg-card border space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{item.serviceName}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-muted-foreground hover:text-destructive rounded-full shrink-0"
                        onClick={() => removeFromCart(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2">
                      {/* Quantity */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Qty</Label>
                        <div className="flex items-center gap-1 mt-1 bg-muted rounded-lg p-0.5">
                          <button 
                            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-background text-lg font-medium"
                            onClick={() => updateQuantity(index, -1)}
                          >−</button>
                          <span className="flex-1 text-center font-bold">{item.quantity}</span>
                          <button 
                            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-background text-lg font-medium"
                            onClick={() => updateQuantity(index, 1)}
                          >+</button>
                        </div>
                      </div>
                      
                      {/* Sell Price */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Sell ₹</Label>
                        <Input
                          type="number"
                          value={item.unitPrice || ""}
                          onChange={(e) => updateItemPrice(index, 'unitPrice', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="h-9 mt-1 text-center font-semibold rounded-lg"
                        />
                      </div>
                      
                      {/* Cost Price */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Cost ₹</Label>
                        <Input
                          type="number"
                          value={item.costPrice || ""}
                          onChange={(e) => updateItemPrice(index, 'costPrice', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="h-9 mt-1 text-center rounded-lg"
                        />
                      </div>
                      
                      {/* Total */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Total ₹</Label>
                        <Input
                          type="number"
                          value={item.totalPrice || ""}
                          onChange={(e) => updateItemTotal(index, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="h-9 mt-1 text-center font-bold text-primary rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Remarks */}
          {cart.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Remarks (Optional)</Label>
              <Textarea
                placeholder="Add notes..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="min-h-[60px] rounded-xl resize-none"
                rows={2}
              />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      {cart.length > 0 && (
        <div className="pt-3 sm:pt-4 border-t space-y-3 sm:space-y-4 shrink-0">
          {/* Payment Mode */}
          <div className="flex gap-1.5 sm:gap-2">
            {paymentModes.map((mode) => (
              <button
                key={mode.id}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 sm:py-3 rounded-xl border-2 transition-all font-medium text-sm sm:text-base",
                  paymentMode === mode.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:border-primary/30"
                )}
                onClick={() => setPaymentMode(mode.id)}
              >
                <mode.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden xs:inline">{mode.label}</span>
              </button>
            ))}
          </div>

          {/* Total & Submit */}
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground">Total (Editable)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <Input
                  type="number"
                  value={editableTotal}
                  onChange={(e) => handleTotalEdit(e.target.value)}
                  onFocus={(e) => { setIsEditingTotal(true); e.target.select(); }}
                  onBlur={() => setIsEditingTotal(false)}
                  className="pl-8 sm:pl-10 h-12 sm:h-14 text-xl sm:text-2xl font-bold text-primary rounded-xl"
                />
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || cart.length === 0}
              className="h-12 sm:h-14 px-5 sm:px-8 rounded-xl text-base sm:text-lg font-bold gap-1.5 sm:gap-2"
              size="lg"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
              ) : (
                <>
                  <Receipt className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Bill</span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Always use Dialog for consistent responsive behavior
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn(
          "p-4 sm:p-6 gap-0 overflow-hidden flex flex-col",
          "[&>button:last-child]:hidden",
          // Responsive width - full width on mobile, grows on larger screens
          "w-[95vw] max-w-[95vw]",
          "sm:w-[90vw] sm:max-w-xl",
          "md:w-[85vw] md:max-w-2xl",
          "lg:w-[80vw] lg:max-w-3xl",
          "xl:max-w-4xl",
          // Responsive height
          "h-[90vh] max-h-[90vh]",
          "sm:h-[85vh] sm:max-h-[85vh]",
          // Mobile-specific bottom sheet style
          "max-sm:rounded-t-2xl max-sm:rounded-b-none",
          "max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0",
          "max-sm:translate-x-0 max-sm:translate-y-0",
          "max-sm:top-auto max-sm:w-full max-sm:max-w-full"
        )}
      >
        <VisuallyHidden>
          <DialogTitle>New Sale</DialogTitle>
        </VisuallyHidden>
        {ModalContent}
      </DialogContent>
    </Dialog>
  );
}